import { computed, reactive, shallowRef, type WritableComputedRef } from 'vue'
import type { VueLogger } from 'vue-logger-plugin'

import { MANUAL_PHRASE_SPLIT_KEY, SING_NOW_POPOVER_SHOW } from '@/constants/practiceConstants'
import store from '@/store'
import { addSoundLevel as processSoundLevel } from '@/util/AudioUtil'
import { defaultRecorderServiceOptions, RecorderService, recordingErrorDialog } from '@/util/RecorderService'
import { isReadyForRecording as validateReadyForRecording, READ, VoiceStyles, VoiceStyleSet } from '@/util/VoiceStyles'

/** Helper to create a writable computed that delegates to a reactive state property. */
function writableComputed<T>(getter: () => T, setter: (v: T) => void): WritableComputedRef<T> {
  return computed({ get: getter, set: setter })
}

export interface AudioRecordingState {
  autoSegmentDetect: boolean
  backgroundSampling: boolean
  backgroundSamplingPercent: number
  disableAutomaticVoiceStartStop: boolean
  displayGraph: boolean
  manualSegmentSplitOffsets: number[]
  nSoundUpdates: number
  nTriggerValues: number
  optionalFilename: string
  pendingUploadRecording: File | null
  showStartNow: boolean
  silenceStartedAt: Date | null
  soundValues: Array<{ x: number; y: number }>
  startRecordingAt: Date | null
  triggerValues: Array<{ x: number; y: number }>
  uploadWaiting: boolean
  voiceStyleSet: VoiceStyleSet
}

export function useAudioRecording(logger: VueLogger) {
  const state = reactive<AudioRecordingState>({
    autoSegmentDetect: false,
    backgroundSampling: false,
    backgroundSamplingPercent: 0,
    disableAutomaticVoiceStartStop: false,
    displayGraph: false,
    manualSegmentSplitOffsets: [],
    nSoundUpdates: 0,
    nTriggerValues: 0,
    optionalFilename: '',
    pendingUploadRecording: null,
    showStartNow: false,
    silenceStartedAt: null,
    soundValues: [],
    startRecordingAt: null,
    triggerValues: [],
    uploadWaiting: false,
    voiceStyleSet: {
      haftarahMelody: null,
      pronunciation: null,
      sentenceGroupMelody: null,
      tags: null,
      torahMelody: null,
      voice: VoiceStyles[READ],
    },
  })

  const recorderService = shallowRef<RecorderService | null>(null)

  // Store the measured background noise level so we can skip re-measurement
  // when starting a new recording (e.g. continuous mode advancing to next sentence).
  let lastMeasuredBaseLevel = 0
  let showStartNowTimer: ReturnType<typeof setTimeout> | null = null

  // Initialize RecorderService with callbacks that manage this composable's state
  recorderService.value = new RecorderService({
    ...defaultRecorderServiceOptions,
    logger,
    onBaseLevel: (bl: number, perc: number) => {
      state.backgroundSamplingPercent = perc
      if (perc >= 100) {
        lastMeasuredBaseLevel = bl
      }
    },
    onError: (error: Error) => {
      store.dispatch.dialog.show(recordingErrorDialog(error))
    },
    onStart: () => {
      console.log('useAudioRecording: onStart, lastMeasuredBaseLevel =', lastMeasuredBaseLevel)
      if (!lastMeasuredBaseLevel && !state.backgroundSampling) {
        state.backgroundSampling = true
        state.backgroundSamplingPercent = 0
      }
    },
    onStartRecording: () => {
      console.log('useAudioRecording: onStartRecording (actual recording begins)')
      state.backgroundSampling = false
      state.backgroundSamplingPercent = 0
      state.startRecordingAt = new Date()
      state.showStartNow = true
      if (showStartNowTimer) {
        clearTimeout(showStartNowTimer)
      }
      showStartNowTimer = setTimeout(() => {
        showStartNowTimer = null
        state.showStartNow = false
      }, SING_NOW_POPOVER_SHOW)
      console.log('useAudioRecording: isRecording is now', state.startRecordingAt !== null)
    },
    onStopRecording: () => {
      console.log('useAudioRecording: onStopRecording')
      state.startRecordingAt = null
    },
  })

  // Computed properties
  // NOTE: Cannot use recorderService.value?.isRecording() here — RecorderService is a plain
  // class held in a shallowRef, so Vue can't track its internal boolean. Instead, derive from
  // reactive state.startRecordingAt which is set/cleared by the same RS callbacks.
  const isRecording = computed<boolean>(() => {
    return state.startRecordingAt !== null
  })

  const isReadyForRecording = computed<boolean>(() => {
    return validateReadyForRecording(state.voiceStyleSet)
  })

  const chartOptions = computed(() => ({
    animation: {
      duration: 0,
    },
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        text: 'Voice Detection',
      },
    },
    responsive: true,
    responsiveAnimationDuration: 0,
    scales: {
      x: {
        ticks: {
          stepSize: 0.5,
        },
        type: 'linear' as const,
      },
      y: {
        max: 1,
        min: 0,
      },
    },
  }))

  const chartData = computed(() => {
    return {
      datasets: [
        {
          borderColor: 'blue',
          data: state.soundValues,
          label: 'Voice level',
        },
        {
          borderColor: 'red',
          data: state.triggerValues,
          label: 'Trigger threshold',
        },
      ],
    }
  })

  // Utility function for collecting graphing data
  const addSoundLevel = (a: Array<{ x: number; y: number }>, n: number, length: number): void => {
    processSoundLevel(a, n, length, state.startRecordingAt)
  }

  // Recording control methods
  const startRecording = () => {
    if (!recorderService.value) {
      logger.error('RecorderService not initialized')
      return
    }

    try {
      if (recorderService.value.options) {
        recorderService.value.options.voiceActivityDetection = true
      }
      recorderService.value.startRecording(lastMeasuredBaseLevel)
    } catch (error) {
      stopRecording()
      store.dispatch.dialog.show(recordingErrorDialog(error))
    }
  }

  const stopRecording = (): void => {
    if (!recorderService.value) {
      logger.debug('stopRecording, unexpected null recorderService')
      return
    }

    if (showStartNowTimer) {
      clearTimeout(showStartNowTimer)
      showStartNowTimer = null
    }
    state.backgroundSampling = false
    state.backgroundSamplingPercent = 0
    state.startRecordingAt = null
    state.showStartNow = false
    recorderService.value.stopRecording()
  }

  const toggleRecord = (): void => {
    if (isRecording.value || state.backgroundSampling) {
      stopRecording()
    } else {
      startRecording()
    }
  }

  const warnManualSegmentation = (): void => {
    if (!state.disableAutomaticVoiceStartStop) {
      return
    }
    store.dispatch.dialog.show({
      isRejectable: false,
      message: `With automatic voice start/stop recognition disabled, you must press the '${MANUAL_PHRASE_SPLIT_KEY}' key after each phrase. You only need this if you are experiencing recording problems that lead to incorrect sentence phrasing.`,
      title: 'Warning',
    })
  }

  const addManualSegmentSplit = (): void => {
    if (state.startRecordingAt) {
      // Offset must be >= 1ms — a 0ms offset produces a nonsensical negative slice in the backend's
      // audio splitter (audio[0 : -KEYBOARD_CLICK_DURATION/2]).
      state.manualSegmentSplitOffsets.push(Math.max(1, new Date().getTime() - state.startRecordingAt.getTime()))
    }
  }

  const resetRecordingState = (): void => {
    state.soundValues.splice(0, state.soundValues.length)
    state.triggerValues.splice(0, state.triggerValues.length)
    state.manualSegmentSplitOffsets.splice(0, state.manualSegmentSplitOffsets.length)
    state.nSoundUpdates = 0
    state.nTriggerValues = 0
    state.startRecordingAt = null
    state.silenceStartedAt = null
    lastMeasuredBaseLevel = 0
  }

  /** Merge partial updates into the voiceStyleSet. */
  const updateVoiceStyleSet = (partial: Partial<VoiceStyleSet>): void => {
    Object.assign(state.voiceStyleSet, partial)
  }

  return {
    // Methods
    addManualSegmentSplit,
    addSoundLevel,
    // State — read-only computeds for properties only the composable controls
    autoSegmentDetect: computed(() => state.autoSegmentDetect),

    backgroundSampling: computed(() => state.backgroundSampling),

    backgroundSamplingPercent: computed(() => state.backgroundSamplingPercent),

    chartData,

    chartOptions,

    // Writable computeds for properties consumers need to set
    disableAutomaticVoiceStartStop: writableComputed(
      () => state.disableAutomaticVoiceStartStop,
      (v: boolean) => {
        state.disableAutomaticVoiceStartStop = v
      },
    ),

    displayGraph: writableComputed(
      () => state.displayGraph,
      (v: boolean) => {
        state.displayGraph = v
      },
    ),

    isReadyForRecording,
    isRecording,
    manualSegmentSplitOffsets: computed(() => state.manualSegmentSplitOffsets),
    optionalFilename: computed(() => state.optionalFilename),
    pendingUploadRecording: writableComputed(
      () => state.pendingUploadRecording,
      (v: File | null) => {
        state.pendingUploadRecording = v
      },
    ),
    recorderService: computed(() => recorderService.value),
    resetRecordingState,
    showStartNow: computed(() => state.showStartNow),
    soundValues: computed(() => state.soundValues),
    startRecording,
    stopRecording,
    toggleRecord,
    triggerValues: computed(() => state.triggerValues),
    updateVoiceStyleSet,
    uploadWaiting: writableComputed(
      () => state.uploadWaiting,
      (v: boolean) => {
        state.uploadWaiting = v
      },
    ),
    voiceStyleSet: computed(() => state.voiceStyleSet),
    warnManualSegmentation,
  }
}
